import { MAX_GRID_DEPTH } from "../inventory/nesting.js";
import { ALL_ITEM_DEFS, getItemDef } from "../items/db.js";
import { mintFactoryMods } from "../items/mods.js";
import { ATTACH_KINDS, MAX_MOD_DEPTH, isQuestItem, type ItemInstance } from "../items/types.js";
import { MAX_LEVEL } from "../meta/progress.js";
import { baseValueEur } from "./prices.js";

/**
 * The traders. A trader is a spread (`buyMult` over base value), a shelf
 * (`offers`) and a buying policy (`buys` names the categories he takes, each
 * at its own rate; `favors` names single items he pays over the odds for).
 * The spread is the economy: buying at 1.5× and selling at 0.5× makes owned kit
 * worth more than its cash. Selling happens at the right counter — the Wirtin
 * will not look at a rifle, El Gato pays most for the gun and Graggy for its
 * parts. Some things are found-only (the hunting rifle, armour, the can, the
 * drum, the six hideout materials). Cash is not merchandise — it is credited at
 * face value by the settlement, never sold. A quest item belongs to no category
 * and crosses only the quest counter (`applyQuestTurnIn`).
 */

export interface TraderOffer {
  defId: string;
  /**
   * Account level this line appears at; absent = 1. The one thing money cannot
   * buy — the shop will not shortcut the ladder (a found rifle is still a
   * rifle). Refused with `invResult` reason `"level"`; the client greys the row.
   */
  minLevel?: number;
  /**
   * Loyalty tier this line opens at, 2..`MAX_TIER`; absent = 1. A level gate is
   * about the account, a tier gate about the man — reputation does not travel.
   * Refused with `"tier"`, greyed with the tier's name (`TIER_NAMES`).
   */
  minTier?: number;
  /**
   * A quest of the SAME trader that must be done first; absent = no gate.
   * `validateShelfUnlocks` (in `economy/loyalty.ts`, to keep the module graph
   * acyclic) proves it is this trader's. Refused with `"quest"`.
   */
  afterQuest?: string;
}

// ---------------------------------------------------------------------------
// Loyalty tiers
// ---------------------------------------------------------------------------

/**
 * Four tiers of standing at one counter — short enough to hold in your head.
 * The rep is derived, never stored (`economy/loyalty.ts`).
 */
export const MAX_TIER = 4;

/**
 * Rep needed to reach each tier, index 0 = tier 2. Against quests worth 30 and
 * 50 € of turnover worth 1: the first rung is two quests or 2 500 € or a mix.
 */
export const TIER_REP: readonly number[] = [50, 150, 400];

/** Tier names, index = tier − 1. Shown on the shelf and in the refusal. */
export const TIER_NAMES: readonly string[] = ["Stranger", "Acquaintance", "Regular", "Trusted"];

/**
 * What `rep` is worth, in [1, MAX_TIER]. Total, like `levelFor`: garbage is a
 * Stranger.
 */
export function tierFor(rep: number): number {
  const v = typeof rep === "number" && Number.isFinite(rep) && rep > 0 ? rep : 0;
  let tier = 1;
  for (const need of TIER_REP) {
    if (v < need) break;
    tier += 1;
  }
  return tier;
}

/**
 * Rep that reaches `tier`, or `null` for tier 1 (already had — no bar to draw)
 * and anything above `MAX_TIER`.
 */
export function repToReachTier(tier: number): number | null {
  if (!Number.isInteger(tier) || tier <= 1 || tier > MAX_TIER) return null;
  return TIER_REP[tier - 2] as number;
}

/**
 * What the buyer brings to the counter — everything the three gates check.
 * One struct built once per shelf; `isQuestDone` is a closure so each side
 * answers out of what it already holds.
 */
export interface OfferStanding {
  level: number;
  tier: number;
  isQuestDone: (questId: string) => boolean;
}

/**
 * Which gate is shut — the same strings the server sends as `invResult`
 * reasons.
 */
export type OfferLock = "level" | "tier" | "quest";

/**
 * The first unmet gate, or `null`. Order is the contract — level → tier →
 * quest, coarsest first — so the client's greyed reason is the server's
 * refusal. Total: a garbage gate is no gate (`validateTraders` catches content).
 */
export function offerLock(offer: TraderOffer, standing: OfferStanding): OfferLock | null {
  const minLevel = offer.minLevel;
  if (typeof minLevel === "number" && Number.isFinite(minLevel) && standing.level < minLevel) {
    return "level";
  }
  const minTier = offer.minTier;
  if (typeof minTier === "number" && Number.isFinite(minTier) && standing.tier < minTier) {
    return "tier";
  }
  const questId = offer.afterQuest;
  if (typeof questId === "string" && questId.length > 0 && !standing.isQuestDone(questId)) {
    return "quest";
  }
  return null;
}

/**
 * What a trader thinks he is being shown. Coarse on purpose — a rule a player
 * can act on in a raid. Six fall out of `kind`; the three that split `loot`
 * (provisions, valuables, materials) need a table.
 */
export type BuyCategory =
  | "guns"
  | "ammo"
  | "mods"
  | "armor"
  | "meds"
  | "gear"
  | "provisions"
  | "valuables"
  | "materials";

/**
 * The three-way split of `loot`. Own-property guarded like `BASE_VALUE_EUR`.
 * `euro` is deliberately absent — cash is not merchandise.
 */
const LOOT_CATEGORY: Readonly<Record<string, BuyCategory>> = {
  // Things somebody in the valley wants to eat, drink or smoke.
  speck: "provisions",
  bergkaese: "provisions",
  honigglas: "provisions",
  schnaps: "provisions",
  zigaretten: "provisions",
  // Game: a provision nobody sells, so it only reaches a counter over the
  // hills.
  meat_hare: "provisions",
  meat_venison: "provisions",

  // Things worth a detour, and worth nothing at all to anybody but the Wirtin.
  taschenuhr: "valuables",
  silberkreuz: "valuables",
  jagdmesser: "valuables",
  // The boss's trophy — the Wirtin is its only buyer.
  gamsbart: "valuables",
  // The chamois trophy, and the Gamsbart's poor relation: shot rather than
  // taken off a man, but sold across the same counter for the same reason.
  gamskrickel: "valuables",

  // Hardware — hideout materials and the small stuff that goes with them.
  naegel: "materials",
  zuendkerzen: "materials",
  kupferdraht: "materials",
  batterie: "materials",
  schraubenzieher: "materials",
  benzinkanister: "materials",
  feuerzeug: "materials",
  streichhoelzer: "materials",
};

/**
 * Which counter `defId` belongs at, or `null` for something nobody trades
 * (unknown id, a loot item missing from the table, cash, quest items). Derived
 * from the item DB so a new gun is a gun at every counter.
 */
export function buyCategoryOf(defId: string): BuyCategory | null {
  const def = getItemDef(defId);
  if (def === null) return null;

  switch (def.kind) {
    case "weapon":
      return "guns";
    case "ammo":
      return "ammo";
    case "attachment":
      return "mods";
    case "armor":
      return "armor";
    case "med":
      return "meds";
    case "container":
      return "gear";
    case "grenade":
      // Ordnance sits on the ammo shelf: the same trader who sells the rounds.
      return "ammo";
    case "loot":
      if (!Object.prototype.hasOwnProperty.call(LOOT_CATEGORY, defId)) return null;
      return LOOT_CATEGORY[defId] ?? null;
  }
}

export interface TraderDef {
  id: string;
  /** Display name. German, Tirolean, a person rather than a shop. */
  name: string;
  /** One line of who they are, shown above their shelf. */
  blurb: string;
  /** The player BUYS at `ceil(base * qty * buyMult)`. Always >= 1. */
  buyMult: number;
  /** What he pays, per category: `floor(base * qty * rate)`. A category not
   *  listed is a category he will not take, whatever it is worth. */
  buys: Readonly<Partial<Record<BuyCategory, number>>>;
  /** Item-specific rates that beat the category rate — the signature trades. */
  favors?: Readonly<Record<string, number>>;
  /** What is on the shelf, in the order it is displayed. */
  offers: readonly TraderOffer[];
}

/**
 * One shelf entry as authored: a bare def id for the ungated common case, or a
 * gated `TraderOffer`.
 */
type OfferSpec = string | TraderOffer;

function offers(...specs: readonly OfferSpec[]): readonly TraderOffer[] {
  // The object form is COPIED rather than passed through, so an authored offer
  // and the one on the shelf are never the same mutable object.
  return specs.map((s) => (typeof s === "string" ? { defId: s } : { ...s }));
}

/** A shelf line that only appears once the player has earned it. */
function atLevel(defId: string, minLevel: number): TraderOffer {
  return { defId, minLevel };
}

/** …once the player has earned it WITH THIS MAN. `minTier` is 2..MAX_TIER; a
 *  gate at 1 is no gate, and the bare string spells that. */
function atTier(defId: string, minTier: number): TraderOffer {
  return { defId, minTier };
}

/** …once one of this trader's own errands is done. */
function afterQuest(defId: string, questId: string): TraderOffer {
  return { defId, afterQuest: questId };
}

/**
 * …once ALL of it holds — the general form for lines with more than one gate.
 */
function gated(defId: string, gates: Omit<TraderOffer, "defId">): TraderOffer {
  return { defId, ...gates };
}

/**
 * Hunter, gamekeeper and the closest thing to a gunsmith. The dearest spread —
 * the only source of guns and cartridges — and a poor buyer.
 */
export const TRADER_HEGEMEISTER: TraderDef = {
  id: "hegemeister",
  name: "Gamekeeper Ferdl",
  blurb:
    "He sits in the hunting lodge above the village and has the right cartridge for every barrel. His prices are high and his offers are low.",
  buyMult: 1.6,
  buys: { guns: 0.45, ammo: 0.45, mods: 0.45 },
  offers: offers(
    "carbine",
    "shotgun",
    "smg",
    "pistol",
    "ammo_8x57_fmj",
    "ammo_8x57_sp",
    "ammo_12g_buck",
    "ammo_12g_slug",
    "ammo_9x19_fmj",
    "ammo_9x19_hp",
    // The soft-point 7.62 only — a gamekeeper stocks the deer round, not the
    // surplus crate.
    "ammo_762_hp",
    // The right cartridge for every barrel, and the chest to keep them all in.
    "ammobox",
    // The dot and the 2× only. The 4× and 8× stay off every shelf — reach is
    // found, not bought.
    "red_dot",
    "scope_2x",
    "muzzle_brake_8x57",
    "ported_choke_12g",
    "foregrip",
    "recoil_pad",
    // Spare magazines. The factory boxes first, because a gun with no magazine
    // in it is the one thing a gamekeeper can actually fix for you.
    "carbine_mag_30",
    "smg_mag_25",
    "pistol_mag_15",
    "rifle_mag_5",
    "carbine_mag_40",
    "shotgun_tube_8",
    // Barrels a gamekeeper would fit: nothing tactical. The long tube waits
    // until the poachers are cleared out of his preserve.
    afterQuest("carbine_barrel_long", "hege_wilderer"),
    // The match barrel is the one thing on his shelf he made for himself, and he
    // parts with it for somebody he knows rather than for somebody with money.
    atTier("rifle_barrel_match", 2),
  ),
};

/**
 * The Wirtin: bandages, schnapps, and the only buyer of valuables (0.65, the
 * top category rate).
 */
export const TRADER_WIRTIN: TraderDef = {
  id: "wirtin",
  name: "Innkeeper Rosa",
  blurb:
    "She runs the inn on the village square, dresses wounds behind the bar, and never asks where the pocket watch came from.",
  buyMult: 1.4,
  buys: { meds: 0.55, provisions: 0.55, valuables: 0.65 },
  // Game is worth most at the inn — 0.9, the best rate in the valley.
  favors: { meat_hare: 0.9, meat_venison: 0.9 },
  offers: offers(
    "med_verband",
    "med_schiene",
    "med_erstehilfe",
    // The full case comes out from under the bar, not off it. She keeps one for
    // the regulars and one for herself, and a stranger gets a bandage.
    atTier("med_sanikasten", 2),
    // She dresses wounds behind the bar, so she is the one with the case to
    // keep the dressings in.
    "medbox",
    "speck",
    "bergkaese",
    "honigglas",
    "schnaps",
    "zigaretten",
    "streichhoelzer",
    "feuerzeug",
  ),
};

/**
 * The general dealer: vests and packs at middling prices. Hideout materials are
 * found-only, but he buys them at the best rate.
 */
export const TRADER_KRAEMER: TraderDef = {
  id: "kraemer",
  name: "Merchant Alois",
  blurb:
    "He has been clearing out the logistics center at Hoferkreuz since lockdown began. If it was on a pallet, he has it; if it was in a cupboard, he does not.",
  buyMult: 1.5,
  buys: { armor: 0.5, gear: 0.5, materials: 0.55 },
  offers: offers(
    "vest_loden",
    // Gated at both counters that stock it — a gate one trader shrugs at is a
    // walk.
    atLevel("vest_gendarm", 6),
    "helmet_stahl",
    "rig_patronengurt",
    // The chest rig came off the same pallet as the copper wire, and he is not
    // opening that crate for anybody who has not fetched him some.
    afterQuest("rig_brustgurt", "kraemer_draht"),
    "pack_wander",
    "pack_jagd",
    // The big touring pack is the last one on the pallet. He would rather it
    // went to somebody who comes back than to somebody passing through.
    atTier("pack_tourer", 2),
  ),
};

/**
 * A solitary hunter at the end of the forest road: fieldcraft and hunting
 * rather than gunsmithing. Two signature trades — brandy at 0.7 (the best
 * price, better than the Wirtin who sells it) and weapon PARTS at 0.65, the
 * best buyer of them in the valley. Guns at a hunter's 0.5.
 */
export const TRADER_GRAGGY: TraderDef = {
  id: "graggy",
  name: "Graggy",
  blurb:
    "He lives where the forest road ends, knows every trail in the valley, and trades only with people who can find their way back out of the woods.",
  buyMult: 1.5,
  buys: { guns: 0.5, mods: 0.65, provisions: 0.5, gear: 0.5, ammo: 0.4 },
  favors: { schnaps: 0.7 },
  offers: offers(
    "carbine",
    "shotgun",
    "ammo_8x57_sp",
    "ammo_12g_buck",
    "ammo_12g_slug",
    // The hunting load and the ten-round box for the Eastern rifle; not the
    // rifle.
    "ammo_762_hp",
    "ec_mag_10",
    "recoil_pad",
    "rig_patronengurt",
    "pack_jagd",
    // The biggest pack anyone sells — level 8 (thirty cells on day one pays for
    // the next three raids) and tier 2 (he trades with faces he knows).
    gated("pack_expedition", { minLevel: 8, minTier: 2 }),
    "junkbox",
    "med_verband",
    "med_schiene",
    "honigglas",
    "streichhoelzer",
  ),
};

/**
 * An armoured gun dealer: the Hegemeister's arsenal plus buyable armour. The
 * best gun-buyer at 0.6 (making the Hegemeister's 0.45 a decision); parts at
 * 0.55, below Graggy.
 */
export const TRADER_EL_GATO: TraderDef = {
  id: "el-gato",
  name: "El Gato",
  blurb:
    "An armoured tomcat with a well-stocked gun room. He sells dependable firepower and the gear to survive returning fire.",
  buyMult: 1.55,
  buys: { guns: 0.6, ammo: 0.5, mods: 0.55, armor: 0.55 },
  offers: offers(
    "carbine",
    "shotgun",
    "smg",
    "pistol",
    // THE gun of the counter, only here. Level 8: the first gun
    // straightforwardly
    // better than the starter should reward a fortnight.
    atLevel("east_carbine", 8),
    // The other thing only he has. Level 12, the highest gate in the game.
    atLevel("sniper", 12),
    "ammo_8x57_fmj",
    "ammo_8x57_sp",
    "ammo_12g_buck",
    "ammo_12g_slug",
    "ammo_9x19_fmj",
    "ammo_9x19_hp",
    "ammo_762_fmj",
    "ammo_762_hp",
    "vest_loden",
    atLevel("vest_gendarm", 6),
    "helmet_stahl",
    "rig_patronengurt",
    // The Hegemeister's two sights (the overlap is the point), then the rest of
    // the ladder. The 8× is gated at 10, the 4× is not — the ladder stays
    // climbable.
    "red_dot",
    "scope_2x",
    "scope_4x",
    atLevel("scope_8x", 10),
    "muzzle_brake_8x57",
    "ported_choke_12g",
    "foregrip",
    "recoil_pad",
    "carbine_mag_30",
    "smg_mag_25",
    "pistol_mag_15",
    "rifle_mag_5",
    "carbine_mag_40",
    "shotgun_tube_8",
    "ec_mag_30",
    "ec_mag_10",
    // The rails, his alone: the parts that turn a rifle into a system.
    "railed_top_cover",
    "railed_handguard",
    "smg_rail_foreend",
    "slant_brake",
    "ergo_grip",
    "folding_stock",
    "smg_barrel_short",
    "smg_barrel_long",
    "flashlight",
    // The lamp is hardware; the laser is a habit he only teaches people he has
    // dealt with before. Acquaintance and it comes out of the drawer.
    atTier("laser", 2),
    // The sniper rifle's SYSTEM parts — can, rails, folder. The short barrel
    // and
    // the 10× stay off every shelf. The can is tier 3, the highest tier gate:
    // silent AND untraceable is not for a face he cannot place.
    atTier("suppressor_8x57", 3),
    // The rails and the folder are the same rifle's other half, one rung lower —
    // they make it a system, not a secret.
    atTier("sniper_rail_handguard", 2),
    atTier("sniper_chassis_folding", 2),
    // Ordnance, his alone (a gamekeeper does not stock frags). Level 5, the
    // lowest gate; the frag also waits for his errand.
    gated("gren_frag", { minLevel: 5, afterQuest: "el_gato_feldtest" }),
    atLevel("gren_smoke", 5),
    atLevel("gren_flash", 5),
  ),
};

/** Every trader, in display order. */
export const TRADERS: readonly TraderDef[] = [
  TRADER_HEGEMEISTER,
  TRADER_WIRTIN,
  TRADER_KRAEMER,
  TRADER_GRAGGY,
  TRADER_EL_GATO,
];

/** Registry keyed by id — a `Map`, so `getTrader("toString")` is `null`. */
const TRADER_BY_ID: ReadonlyMap<string, TraderDef> = new Map(TRADERS.map((t) => [t.id, t]));

/** Non-throwing lookup. `null` for anything unknown. */
export function getTrader(id: string): TraderDef | null {
  return TRADER_BY_ID.get(id) ?? null;
}

/**
 * True when the trader stocks `defId`. Level-blind: "is it on the shelf" and
 * "may I have it yet" are different refusals (`offerFor`).
 */
export function traderSells(trader: TraderDef, defId: string): boolean {
  for (const offer of trader.offers) {
    if (offer.defId === defId) return true;
  }
  return false;
}

/**
 * The shelf line for `defId`, or `null`. The one accessor for an offer's gates.
 * First match wins; `validateTraders` forbids duplicates.
 */
export function offerFor(trader: TraderDef, defId: string): TraderOffer | null {
  for (const offer of trader.offers) {
    if (offer.defId === defId) return offer;
  }
  return null;
}

/**
 * Units one purchase hands over: a full stack of stackable ammo, one of
 * anything else — a click count, priced accordingly. Unknown ids answer 1.
 */
export function buyQty(defId: string): number {
  const def = getItemDef(defId);
  if (def === null) return 1;
  return def.kind === "ammo" && def.stackMax > 1 ? def.stackMax : 1;
}

/**
 * One unit's value WITH the parts it ships with: `baseValueEur` plus,
 * recursively, every slot's `factoryDefId`. Exists because the v19 carve moved
 * value off the receivers onto the parts — this folds it back (320 + 60 + 40 =
 * 420 € for a carbine). Magazines stay free across the counter: a `mag` slot
 * has no `factoryDefId`, so the walk never sees one. Depth-capped at
 * `MAX_MOD_DEPTH`.
 */
export function factoryValueEur(defId: string): number {
  const walk = (id: string, depth: number): number => {
    const value = baseValueEur(id);
    if (depth >= MAX_MOD_DEPTH) return value;
    const def = getItemDef(id);
    if (def === null || (def.kind !== "weapon" && def.kind !== "attachment")) return value;
    let total = value;
    for (const slot of def.slots ?? []) {
      const factoryId = slot.factoryDefId;
      if (factoryId === undefined) continue;
      total += walk(factoryId, depth + 1);
    }
    return total;
  };
  return walk(defId, 0);
}

/**
 * What one purchase costs, whole Euro, rounded UP. Priced on `factoryValueEur`,
 * since that is what `mintPurchase` hands over.
 */
export function buyPrice(trader: TraderDef, defId: string): number {
  return Math.ceil(factoryValueEur(defId) * buyQty(defId) * trader.buyMult);
}

/**
 * The rate `trader` pays for `defId`, or `null`: a favour beats the category
 * rate; no category (cash, unknowns) or an unlisted one gets no number.
 * "Not interested" and "pays badly" are different sentences.
 */
export function sellMultFor(trader: TraderDef, defId: string): number | null {
  const favors = trader.favors;
  if (favors !== undefined && Object.prototype.hasOwnProperty.call(favors, defId)) {
    return favors[defId] ?? null;
  }
  const category = buyCategoryOf(defId);
  if (category === null) return null;
  return trader.buys[category] ?? null;
}

/**
 * What `trader` pays for ONE instance, whole Euro rounded DOWN, or `null`.
 * Counts the item times its stack plus every fitted attachment to
 * `MAX_MOD_DEPTH` in `ATTACH_KINDS` order. Does NOT count loaded rounds,
 * armour durability, weapon wear or med charges (one multiplier away if
 * wanted; the bench's shrinking ceiling is what keeps a worn gun from being a
 * money printer). The category gate and rate are the base item's: a trader who
 * buys guns buys the scope on it at his gun rate.
 */
export function sellPrice(trader: TraderDef, item: ItemInstance): number | null {
  // A quest item crosses no counter but the quest's own — and at 0 € the sale
  // would silently destroy the only copy.
  if (isQuestItem(getItemDef(item.defId))) return null;

  const mult = sellMultFor(trader, item.defId);
  if (mult === null) return null;

  const value = baseValueEur(item.defId) * item.stack + fittedValue(item, 0);
  return Math.floor(value * mult);
}

/**
 * Value of what is screwed to `item`, recursively. `depth` is the item's own
 * depth (0 = the gun); the walk stops past `MAX_MOD_DEPTH`. Unknown parts are
 * worth 0 and walked no further.
 */
function fittedValue(item: ItemInstance, depth: number): number {
  const mods = item.mods;
  if (mods === undefined || depth >= MAX_MOD_DEPTH) return 0;
  let value = 0;
  for (const slotId of ATTACH_KINDS) {
    const mod = mods[slotId];
    if (mod === undefined) continue;
    value += baseValueEur(mod.defId) * mod.stack;
    value += fittedValue(mod, depth + 1);
  }
  return value;
}

/**
 * What `trader` pays for an instance AND everything inside it, or `null`.
 * Exactly `sellPrice` for an empty container. ALL OR NOTHING: a number only if
 * he takes the box and has a rate for every item in it, however deep — a
 * partial sale would be the till choosing what to leave behind. Quest items
 * and cash poison the whole lot via `sellPrice`. Depth-capped at
 * `MAX_GRID_DEPTH`; a structure past it answers `null`.
 */
export function sellPriceDeep(trader: TraderDef, item: ItemInstance): number | null {
  return deepPrice(trader, item, 0);
}

/** `sellPriceDeep`'s recursion. `depth` is the depth of the GRID `item` sits
 *  in, so an item at `MAX_GRID_DEPTH` may legally hold nothing at all. */
function deepPrice(trader: TraderDef, item: ItemInstance, depth: number): number | null {
  const own = sellPrice(trader, item);
  const contents = item.contents;
  // Empty is absent and absent is empty (see `items/types.ts`), so both spellings
  // of "there is nothing in it" fall through to the flat price.
  if (contents === undefined || contents.items.length === 0) return own;
  if (own === null) return null;
  if (depth >= MAX_GRID_DEPTH) return null;

  let total = own;
  for (const p of contents.items) {
    const inside = deepPrice(trader, p.item, depth + 1);
    if (inside === null) return null;
    total += inside;
  }
  return total;
}

/**
 * The instance a purchase hands over, or `null` for an unknown def. Minted as
 * `rollContainerLoot` mints: full durability/charges, a gun empty but wearing
 * every factory part. Shared so client and server agree on what came out —
 * including how many ids it cost, which is 1 + the factory tree (four for the
 * carbine, eight for the Eastern carbine) and must be asked of
 * `mintFactoryMods`, never assumed.
 */
export function mintPurchase(defId: string, nextIid: () => string): ItemInstance | null {
  const def = getItemDef(defId);
  if (def === null) return null;

  const item: ItemInstance = { iid: nextIid(), defId: def.id, stack: buyQty(def.id) };
  if (def.kind === "armor") item.durability = def.maxDurability;
  else if (def.kind === "med") item.durability = def.charges;
  else if (def.kind === "weapon") {
    // Spelled out rather than absent (which already reads as full) so the panel
    // shows "100/100" like a repaired gun — one rule for all three wearing
    // kinds.
    item.durability = def.maxDurability;
    // Every factory part, recursively. The magazine among them is empty.
    const mods = mintFactoryMods(def, nextIid);
    if (mods !== undefined) item.mods = mods;
  }
  return item;
}

/**
 * The hideout's shopping list — exactly the list nobody may sell. Named here so
 * the economy keeps its one dependency direction.
 */
const FOUND_ONLY_MATERIALS: readonly string[] = [
  "naegel",
  "zuendkerzen",
  "batterie",
  "kupferdraht",
  "schraubenzieher",
  "benzinkanister",
];

/**
 * Structural check for the shelves, in `validateItemDb`'s shape. A unit test
 * and a boot assertion.
 */
export function validateTraders(): string[] {
  const problems: string[] = [];
  const seenIds = new Set<string>();

  for (const t of TRADERS) {
    const where = `trader "${t.id}"`;
    if (t.id.length === 0) problems.push("a trader has an empty id");
    if (seenIds.has(t.id)) problems.push(`duplicate trader id "${t.id}"`);
    seenIds.add(t.id);
    if (TRADER_BY_ID.get(t.id) !== t) problems.push(`${where} is not the one the registry holds`);

    if (t.name.length === 0) problems.push(`${where} has an empty name`);
    if (t.blurb.length === 0) problems.push(`${where} has an empty blurb`);

    // A trader who sells below base value, or buys above it, is a money
    // printer: the loop is buy from him, sell to him, repeat.
    if (!(t.buyMult >= 1) || !Number.isFinite(t.buyMult)) {
      problems.push(`${where} has buyMult ${t.buyMult} (must be >= 1)`);
    }
    // The KEYS need no checking: they are compile-time `BuyCategory` literals,
    // and a key that is not one is a build failure rather than a boot one.
    const categories = Object.keys(t.buys) as BuyCategory[];
    if (categories.length === 0) problems.push(`${where} buys nothing at all`);
    for (const category of categories) {
      const rate = t.buys[category];
      if (rate === undefined || !(rate > 0) || !(rate < 1)) {
        problems.push(`${where} pays ${rate} for ${category} (must be in (0, 1))`);
      }
    }

    // A favour names one item, so it can name a nonexistent one — or cash.
    for (const [defId, rate] of Object.entries(t.favors ?? {})) {
      if (getItemDef(defId) === null) {
        problems.push(`${where} favours unknown item "${defId}"`);
      } else if (baseValueEur(defId) <= 0) {
        problems.push(`${where} favours "${defId}", which has no price`);
      }
      if (defId === "euro") problems.push(`${where} favours cash`);
      if (!(rate > 0) || !(rate < 1)) {
        problems.push(`${where} favours "${defId}" at ${rate} (must be in (0, 1))`);
      }
    }

    if (t.offers.length === 0) problems.push(`${where} sells nothing`);

    const seenOffers = new Set<string>();
    for (const offer of t.offers) {
      if (seenOffers.has(offer.defId)) problems.push(`${where} lists "${offer.defId}" twice`);
      seenOffers.add(offer.defId);
      if (getItemDef(offer.defId) === null) {
        problems.push(`${where} offers unknown item "${offer.defId}"`);
        continue;
      }
      if (baseValueEur(offer.defId) <= 0) {
        problems.push(`${where} offers "${offer.defId}", which has no price`);
      }
      // Cash on a shelf is an exchange rate, and it is never a good one.
      if (offer.defId === "euro") problems.push(`${where} offers cash`);
      // Above the ladder is unreachable stock; ≤ 0 is an accident. Level 1 is
      // legal and means the same as absent.
      const minLevel = offer.minLevel;
      if (minLevel !== undefined) {
        if (!Number.isInteger(minLevel) || minLevel < 1 || minLevel > MAX_LEVEL) {
          problems.push(
            `${where} gates "${offer.defId}" at level ${minLevel} (must be an integer in 1..${MAX_LEVEL})`,
          );
        }
      }
      // Same rule; unlike level, tier 1 is refused — every account already has
      // it.
      const minTier = offer.minTier;
      if (minTier !== undefined) {
        if (!Number.isInteger(minTier) || minTier < 2 || minTier > MAX_TIER) {
          problems.push(
            `${where} gates "${offer.defId}" at tier ${minTier} (must be an integer in 2..${MAX_TIER})`,
          );
        }
      }
      // Only that the field is non-empty; that it resolves to this trader's
      // quest is `validateShelfUnlocks`'s question (quests.ts imports this
      // file).
      const afterQuestId = offer.afterQuest;
      if (afterQuestId !== undefined && afterQuestId.length === 0) {
        problems.push(`${where} gates "${offer.defId}" behind an empty quest id`);
      }
    }
  }

  // Nothing is dead weight: every item except cash (credited at face value) and
  // quest items (which must have NO buyer) has at least one buyer.
  for (const def of ALL_ITEM_DEFS) {
    if (def.id === "euro") continue;
    if (isQuestItem(def)) {
      if (TRADERS.some((t) => sellMultFor(t, def.id) !== null)) {
        problems.push(`quest item "${def.id}" has a buyer in the valley`);
      }
      if (TRADERS.some((t) => traderSells(t, def.id))) {
        problems.push(`quest item "${def.id}" is on a shelf`);
      }
      continue;
    }
    if (!TRADERS.some((t) => sellMultFor(t, def.id) !== null)) {
      problems.push(`nobody in the valley buys "${def.id}"`);
    }
  }

  // ...and the six hideout materials are found-only, which is only true for as
  // long as nobody quietly puts one back on a shelf.
  const shelved = new Set(TRADERS.flatMap((t) => t.offers.map((o) => o.defId)));
  for (const defId of FOUND_ONLY_MATERIALS) {
    if (shelved.has(defId)) problems.push(`"${defId}" is on a shelf, but it is found-only`);
  }

  return problems;
}
